import { useState } from "react";
import { Check, Eye, LoaderCircle, X } from "lucide-react";
import { getReliabilityViewModel } from "../domain/reliability";
import type { ProfilePreviewResponse } from "../domain/types";
import { t } from "../i18n";
import { proxiedImageUrl } from "../utils/imageProxy";
import { ProfileStatusBadge } from "./ProfileStatusBadge";

interface ProfilePreviewCardProps {
  data: ProfilePreviewResponse;
  onStartMonitoring?: () => void;
  onClose?: () => void;
  ctaLabel?: string;
  loadingLabel?: string;
  isLoading?: boolean;
  authenticated?: boolean;
}

export function ProfilePreviewCard({
  data,
  onStartMonitoring,
  onClose,
  ctaLabel,
  loadingLabel,
  isLoading = false,
  authenticated = false,
}: ProfilePreviewCardProps) {
  const { profile, availability, reliabilityStatus, previewFollowing } = data;
  const reliability = getReliabilityViewModel(reliabilityStatus);
  const [avatarFailed, setAvatarFailed] = useState(false);

  if (availability === "private_not_available") {
    return (
      <div className="st-preview-result-card">
        {onClose && (
          <button
            onClick={onClose}
            className="st-preview-close-button"
            aria-label={t("commonClose")}
          >
            <X size={20} />
          </button>
        )}
        <ProfileStatusBadge status={reliabilityStatus} />
        <h2 className="st-preview-result-title">
          {t("previewPrivateNotAvailable")}
        </h2>
        <p className="st-preview-result-body">{reliability.description}</p>
        <div className="st-preview-note st-preview-note-warning">
          <p>{t("previewPrivateNote")}</p>
        </div>
      </div>
    );
  }

  if (availability === "not_found") {
    return (
      <div className="st-preview-result-card">
        {onClose && (
          <button
            onClick={onClose}
            className="st-preview-close-button"
            aria-label={t("commonClose")}
          >
            <X size={20} />
          </button>
        )}
        <ProfileStatusBadge status={reliabilityStatus} />
        <h2 className="st-preview-result-title">{t("previewNotFound")}</h2>
        <p className="st-preview-result-body">{t("previewNotFoundBody")}</p>
      </div>
    );
  }

  if (availability === "provider_error" || availability === "rate_limited") {
    return (
      <div className="st-preview-result-card">
        {onClose && (
          <button
            onClick={onClose}
            className="st-preview-close-button"
            aria-label={t("commonClose")}
          >
            <X size={20} />
          </button>
        )}
        <ProfileStatusBadge status={reliabilityStatus} />
        <h2 className="st-preview-result-title">
          {availability === "rate_limited"
            ? t("searchErrorRateLimit")
            : t("reliability_provider_error")}
        </h2>
        <p className="st-preview-result-body">{t("searchErrorProvider")}</p>
        <button
          onClick={() => window.location.reload()}
          className="st-preview-ghost-button"
        >
          {t("commonRetry")}
        </button>
      </div>
    );
  }

  return (
    <div className="st-preview-result-card">
      {onClose && (
        <button
          onClick={onClose}
          className="st-preview-close-button"
          aria-label={t("commonClose")}
        >
          <X size={20} />
        </button>
      )}
      <div className="st-preview-centered-head">
        <div className="st-preview-profile-avatar-centered">
          <div className="st-preview-profile-avatar-inner">
            {profile?.profilePicUrl && !avatarFailed ? (
              <img
                src={proxiedImageUrl(profile.profilePicUrl)}
                alt={profile.fullName ?? profile.handle}
                className="st-preview-profile-avatar-img"
                referrerPolicy="no-referrer"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              (profile?.fullName?.charAt(0) ??
              profile?.handle.charAt(0).toUpperCase())
            )}
          </div>
        </div>
        <div className="st-preview-profile-main-centered">
          <h2>
            {profile?.fullName ?? `@${profile?.handle}`}
            {profile?.isVerified && (
              <span className="st-preview-verified" style={{ marginLeft: 8 }}>
                ✓
              </span>
            )}
          </h2>
          <p className="st-preview-profile-handle">@{profile?.handle}</p>
          <div className="st-preview-profile-stats">
            {profile?.followingCount !== undefined && (
              <span>
                <strong>{profile.followingCount.toLocaleString()}</strong>{" "}
                {t("commonFollowing")}
              </span>
            )}
            {profile?.followersCount !== undefined && (
              <span>
                <strong>{profile.followersCount.toLocaleString()}</strong>{" "}
                {t("commonFollowers")}
              </span>
            )}
          </div>
        </div>
      </div>

      {previewFollowing && previewFollowing.length > 0 && (
        <div className="st-preview-follow-list-wrap">
          <div className="st-preview-section-heading">
            <span className="st-preview-section-icon" aria-hidden="true">
              <Eye size={17} />
            </span>
            <div>
              <h3>{t("previewBaselineTitle")}</h3>
              <p>{t("previewBaselineBody")}</p>
            </div>
          </div>
          <div className="st-preview-follow-list">
            {previewFollowing.slice(0, 10).map((f) => (
                <div key={f.handle} className="st-preview-follow-item">
                  <div className="st-preview-follow-avatar">
                    {f.profilePicUrl ? (
                      <img
                        src={proxiedImageUrl(f.profilePicUrl)}
                        alt=""
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="st-preview-follow-avatar-placeholder">
                        {f.fullName?.charAt(0) ??
                          f.handle.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="st-preview-follow-meta">
                    <p>{f.fullName ?? f.handle}</p>
                    <small>@{f.handle}</small>
                  </div>
                  {f.isVerified && (
                    <span className="st-preview-verified st-preview-verified-small">
                      ✓
                    </span>
                  )}
                </div>
            ))}
          </div>
        </div>
      )}

      <div className={`st-preview-lock-box-premium${authenticated ? " is-authenticated" : ""}`}>
        <h3>{t(authenticated ? "previewAuthenticatedTitle" : "previewLockedTitle")}</h3>
        <p>{t(authenticated ? "previewAuthenticatedBody" : "previewLockedBody")}</p>
        <ul>
          <li>
            <Check size={16} />
            <span>{t("previewFeature1")}</span>
          </li>
          <li>
            <Check size={16} />
            <span>{t("previewFeature2")}</span>
          </li>
          <li>
            <Check size={16} />
            <span>{t("previewFeature3")}</span>
          </li>
          <li>
            <Check size={16} />
            <span>{t("previewFeature4")}</span>
          </li>
        </ul>
        <p className="st-preview-lock-caption">
          {t("previewMonitoringStartsAfter")}
        </p>
        {onStartMonitoring && (
          <button
            type="button"
            onClick={onStartMonitoring}
            className="st-preview-primary-button"
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading && (
              <LoaderCircle
                className="st-preview-button-spinner"
                size={17}
                aria-hidden="true"
              />
            )}
            {isLoading
              ? loadingLabel ?? t("commonLoading")
              : ctaLabel ?? t("previewUnlockNow")}
          </button>
        )}
      </div>
    </div>
  );
}
